import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
import rdvImg from '../../assets/rdv-logoW.png';
import rdvBallImg from '../../assets/rdv-logoBall.png';
import { useFileContext } from '../../ContextAPI/FileContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import PermissionGate from '../PermissionGate/PermissionGate';
import { useDownloads } from '../../contexts/DownloadsContext';
import { baseApi } from '../../API/api';

const Sidebar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { logout, userEmail } = useFileContext();
  const { permissions, loading } = useUserPermissions();
  
  // Debug: Log permissions when they change
  useEffect(() => {
    if (permissions) {
      console.log('[Sidebar] Permissions loaded:', permissions);
      console.log('[Sidebar] User groups:', permissions.groups);
      console.log('[Sidebar] Has developers group:', permissions.groups?.includes('developers'));
    }
  }, [permissions]);
  const { togglePopup, downloads, isPopupOpen } = useDownloads();
  const emailRef = useRef(null);
  const sidebarListRef = useRef(null);
  const [dynamicWidth, setDynamicWidth] = useState(235);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [useCollapsedLogo, setUseCollapsedLogo] = useState(false);
  
  // Automatically collapse sidebar when /view route is opened
  useEffect(() => {
    if (currentPath.endsWith('/view')) {
      if (!isCollapsed) {
        setIsCollapsed(true);
        setUseCollapsedLogo(true);
      }
    }
  }, [currentPath, isCollapsed]);
  
  // Calculate dynamic width based on longest li text width and collapsed state
  const calculateWidth = () => {
    if (isCollapsed) {
      setDynamicWidth(80); // Collapsed width for icons only
      return;
    }
    
    if (sidebarListRef.current) {
      const listItems = sidebarListRef.current.querySelectorAll('li a span');
      let maxWidth = 0;
      
      const baseWidth = 235; // Base width in pixels
      const padding = 80; // Reduced padding for other elements (avatar, margins, etc.)
      const minWidth = baseWidth;
      const maxWidthLimit = 600; // Maximum width to prevent excessive expansion
      
      // Create a temporary span to measure text width
      const tempSpan = document.createElement('span');
      tempSpan.style.fontFamily = 'Montserrat, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';
      tempSpan.style.fontSize = '14px'; // Match the CSS font-size for nav links
      tempSpan.style.fontWeight = '300'; // Match the CSS font-weight
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      
      // Measure each list item's text width
      listItems.forEach((span) => {
        if (span.textContent) {
          tempSpan.textContent = span.textContent;
          document.body.appendChild(tempSpan);
          const textWidth = tempSpan.offsetWidth;
          document.body.removeChild(tempSpan);
          maxWidth = Math.max(maxWidth, textWidth);
        }
      });
      
      const calculatedWidth = Math.min(Math.max(maxWidth + padding, minWidth), maxWidthLimit);
      setDynamicWidth(calculatedWidth);
    }
  };

  // Calculate width when collapsed state changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      calculateWidth();
    }, 0);
    return () => clearTimeout(timer);
  }, [isCollapsed]); // Recalculate when collapsed state changes



  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    // Immediately switch logo to match target state
    setUseCollapsedLogo(next);
  };

  // Function to get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 6 && hour < 12) {
      return 'Good morning!';
    } else if (hour >= 12 && hour < 18) {
      return 'Good afternoon!';
    } else {
      return 'Good evening!';
    }
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`} style={{ width: `${dynamicWidth}px` }}>
      <div className='sidebarHeader file-selector'>
        <div className='sidebarHeaderLogout'>
          <div className='header-content'>
             <div className='sidebar-avatar'>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {!isCollapsed && (
              <div className='user-info'>
                <p className='user-name'>{getTimeBasedGreeting()}</p>
                <p className='user-email' ref={emailRef}>
                  {(userEmail || 'shafrir.kashi@rdvsystems.com').split('@')[0]}
                </p>
              </div>
            )}
          </div>
          {/* Toggle Button */}
          <button className='sidebar-toggle' onClick={toggleSidebar} title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <i className={`fas ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
          </button>
        </div>
        <ul ref={sidebarListRef}>
          {/* Basic user access - everyone can see these */}
          <li>
            <Link 
              to='/projects' 
              className={currentPath === '/projects' ? 'active' : ''}
              title={isCollapsed ? 'Projects' : ''}
            >
              <i className='fas fa-folder-open icon'></i>
              {!isCollapsed && <span>Projects</span>}
            </Link>
          </li>
          {/* Developers group access only */}
          <PermissionGate group="developers">
            <li>
              <Link 
                to='/RSA-Analysis' 
                className={currentPath === '/RSA-Analysis' ? 'active' : ''}
                title={isCollapsed ? 'RSA Analysis' : ''}
              >
                <i className='fa-solid fa-chart-simple icon'></i>
                {!isCollapsed && <span>RSA Analysis</span>}
              </Link>
            </li>
          </PermissionGate>
          
          {/* Developers group access only */}
          {/* <PermissionGate group="developers">
            <li>
              <Link 
                to='/magic-fixer' 
                className={currentPath === '/magic-fixer' ? 'active' : ''}
                title={isCollapsed ? 'Magic Fixer' : ''}
              >
                <i className='fas fa-magic icon'></i>
                {!isCollapsed && <span>Magic Fixer</span>}
              </Link>
            </li>
          </PermissionGate> */}
        </ul>
      </div>
      
      {/* Profile buttons */}
      <div className='user-profile-buttons'>
        <button 
          className={`profile-button downloads ${isPopupOpen ? 'has-downloads' : ''}`}
          onClick={togglePopup}
          title={isCollapsed ? 'Downloads' : ''}
        >
          <div className='downloads-button-content'>
            <i className='fas fa-download'></i>
            {!isCollapsed && <span>Downloads</span>}
          </div>
          {downloads.length > 0 && (
            <span className='downloads-badge'>{downloads.length}</span>
          )}
        </button>
        
        <button className='profile-button settings' title={isCollapsed ? 'Settings' : ''}>
          <i className='fas fa-cog'></i>
          {!isCollapsed && <span>Settings</span>}
        </button>
        
        <button className='profile-button logout' onClick={handleLogout} title={isCollapsed ? 'Logout' : ''}>
          <i className='fas fa-sign-out-alt'></i>
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
      
      {/* Logo at the bottom */}
      <div className='sidebar-footer'>
        <div className='logo'>
          <img src={rdvImg} alt='Logo' className={`logo-wide ${useCollapsedLogo ? 'is-hidden' : 'is-shown'}`} />
          <img src={rdvBallImg} alt='Logo' className={`logo-ball ${useCollapsedLogo ? 'is-shown' : 'is-hidden'}`} />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

